import React from "react";
import { Column, DataTable } from "@/components/ui/data-table";
import { CircleAlert, CircleUserIcon } from "lucide-react";
import { Request, RequestStatus, RequestType } from "@/types/request";
import { PriorityStar } from "@/components/common/PriorityStar";
import { RequestStatusSelect } from "./RequestStatusSelect";
import { useTranslation } from "react-i18next";
import { DateCell } from "@/components/pages/settlement/components/table-cells/DateCell";
import { Avatar } from "@radix-ui/react-avatar";
import { AvatarFallback } from "@/components/ui/avatar";
import { Task } from "@/types/tasks";
import { useUpdateTask } from "@/components/pages/requests/hooks/tasks.hooks";
import AssignedStaffCell from "@/components/common/AssignedStaffCell";
import AssignStaffModal from "./modals/AssignStaffModal";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckboxSelectionResult } from "@/hooks/useCheckboxSelection";
import { cn } from "@/lib/utils";
import { RequestActionsCell } from "@/components/pages/reservations/components/cells/RequestActionsCell";
import { PaymentStatusCell } from "@/components/pages/reservations/components/cells/PaymentStatus";
import { BillingPaymentStatus } from "@/types/billing-transactions";
import { getTaskAssignment } from "../utils/taskAssignment";
import { RequestGuestName } from "./RequestGuestName";
import {
  getEffectiveRequestPaymentStatus,
  isRequestPaymentRequired,
} from "../utils/paymentStatus";
import {
  getRoomUpgradeRoomLabel,
  isRoomUpgradeRequest,
} from "../utils/roomUpgradeRequest";
import { getRequestDisplayTitle } from "../utils/requestDisplay";
import { isRequestOverdue } from "../utils/requestDelay";
import { RequestDelayBadge } from "./RequestDelayBadge";

export type RequestColumnKey =
  | "select"
  | "priority"
  | "guest.profile"
  | "booking"
  | "service"
  | "createdAt"
  | "dueDateTime"
  | "status"
  | "assignedStaff"
  | "createdBy"
  | "paymentStatus"
  | "actions";

export const RequestsTable: React.FC<{
  requests: Request[];
  visibleColumns: RequestColumnKey[];
  activeTab?: RequestType;
  isLoading: boolean;
  currentPage?: number;
  totalPages?: number;
  sortField?: string | null;
  sortDirection?: "asc" | "desc" | null;
  onPageChange?: (page: number) => void;
  onSort?: (field: string) => void;
  onRowClick: (row: Request) => void;
  onUpdateRequest: (requestId: string, updatedData: Partial<Request>) => void;
  onEditRequest?: (
    request: Request,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
  withPagination?: boolean;
  withSorting?: boolean;
  selection: CheckboxSelectionResult;
  serviceCellClassName?: string;
  dataCellClassName?: string;
  statusCellClassName?: string;
  statusCellIconClassName?: string;
  assignedStaffClassName?: string;
  teamClassName?: string;
  staffClassName?: string;
  assignedStaffIconSize?: number;
  assignedStaffShowCircle?: boolean;
  notAssignedStaffClassName?: string;
  createdByClassName?: string;
  isChatWithGuest?: boolean;
  showChatAction?: boolean;
  applyStatusBadge?: boolean;
  applyPaymentStatusBadge?: boolean;
  actionsEditIcon?: React.ReactNode;
  starClassName?: string;
  priorityStarClassName?: string;
  showStatusIcon?: boolean;
}> = ({
  requests,
  visibleColumns,
  isLoading,
  currentPage,
  totalPages,
  sortField,
  sortDirection,
  onPageChange,
  onSort,
  onRowClick,
  onUpdateRequest,
  onEditRequest,
  withPagination = true,
  withSorting = true,
  selection: {
    selectAll,
    toggleSelectAll,
    toggleSelectItem: toggleSelectRequest,
    selectedItems,
  },
  serviceCellClassName,
  dataCellClassName,
  statusCellClassName,
  statusCellIconClassName,
  assignedStaffClassName,
  teamClassName,
  staffClassName,
  assignedStaffIconSize,
  assignedStaffShowCircle,
  notAssignedStaffClassName,
  actionsEditIcon,
  isChatWithGuest,
  showChatAction,
  createdByClassName,
  starClassName,
  priorityStarClassName,
  applyStatusBadge,
  applyPaymentStatusBadge,
  showStatusIcon,
}) => {
  const { t } = useTranslation();

  const updateTaskMutation = useUpdateTask();

  const allColumns: Record<RequestColumnKey, Column<Request>> = {
    select: {
      key: "select",
      header: (
        <Checkbox checked={selectAll} onCheckedChange={toggleSelectAll} />
      ),
      className: "text-center",
      render: (_value: unknown, request: Request) => (
        <div className="flex justify-center">
          <Checkbox
            checked={selectedItems.includes(String(request.id))}
            onCheckedChange={() => {
              toggleSelectRequest(String(request.id));
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ),
    },
    priority: {
      key: "priority",
      className: "text-center",
      header: t("requests.table.priority"),
      render: (value, row) => (
        <PriorityStar
          priority={Boolean(value)}
          id={row.id || ""}
          onPriorityChange={(id, priority) => onUpdateRequest(id, { priority })}
          starClassName={starClassName}
          priorityStarClassName={priorityStarClassName}
        />
      ),
    },
    "guest.profile": {
      key: "guest.profile",
      className: "text-center",
      header: t("requests.table.name"),
      render: (_value, row) => {
        const guestProfile = row.guest?.profile || row.booking?.guest?.profile;

        return guestProfile ? (
          <div className="flex flex-col items-center gap-1">
            <RequestGuestName
              firstName={guestProfile.firstName}
              lastName={guestProfile.lastName}
              countryCode={guestProfile.language}
              className="justify-center"
            />
            {guestProfile.language ? (
              <span className="text-xs text-gray-500">
                {guestProfile.language}
              </span>
            ) : null}
          </div>
        ) : null;
      },
      sortable: true,
    },
    booking: {
      key: "booking",
      className: "text-center",
      header: t("requests.table.room"),
      render: (value, row) => {
        if (isRoomUpgradeRequest(row)) {
          return (
            <span className="text-sm font-medium">
              {getRoomUpgradeRoomLabel(row)}
            </span>
          );
        }

        const roomNumber = value?.room?.number || row.room?.number;

        return (
          <>
            {roomNumber && (
              <span className="text-sm font-medium">{roomNumber}</span>
            )}
          </>
        );
      },
      sortable: true,
    },
    service: {
      key: "service",
      className: "text-center",
      header: t("requests.table.service"),
      render: (value, row) => (
        <span className={cn("text-sm font-medium", serviceCellClassName)}>
          {getRequestDisplayTitle(row, t("requests.form.noService"))}
        </span>
      ),
      sortable: true,
    },
    createdAt: {
      key: "createdAt",
      className: "text-center",
      header: t("requests.table.creationTime"),
      render: (value) => {
        return <DateCell date={value} className={dataCellClassName} />;
      },
      sortable: true,
    },
    dueDateTime: {
      key: "dueDateTime",
      className: "text-center",
      header: t("requests.table.dueDate"),
      render: (value, row) => {
        const isOverdue = isRequestOverdue(row);

        return (
          <div className="flex flex-col items-center gap-1">
            <DateCell date={value} className={dataCellClassName} />
            {isOverdue ? <RequestDelayBadge /> : null}
          </div>
        );
      },
      sortable: true,
    },
    status: {
      key: "status",
      className: "text-center",
      header: t("requests.table.status"),
      render: (_value, row) => {
        const task = row.tasks?.[0];
        const currentStatus =
          task?.status || row.status || RequestStatus.NEW_TASK;

        return (
          <div className="flex items-center justify-center">
            <RequestStatusSelect
              currentStatus={currentStatus}
              requestId={row.id || ""}
              onStatusChange={(_, status) => {
                if (task?.id) {
                  updateTaskMutation.mutateAsync({
                    id: task.id,
                    data: { status: status as RequestStatus },
                  });
                }
              }}
              className={statusCellClassName}
              iconClassName={statusCellIconClassName}
              applyBadge={applyStatusBadge}
              showIcon={showStatusIcon}
            />
          </div>
        );
      },
      sortable: true,
    },
    assignedStaff: {
      key: "assignedStaff",
      className: "text-center",
      header: t("requests.table.assignedStaff"),
      render: (_value, row) => {
        const task: Task | undefined = row.tasks?.[0];
        const {
          assignedTeamId,
          assignedTeamName,
          assignedStaffId,
          assignedStaffName,
        } = getTaskAssignment(task, row.service);

        const assignedStaffCell = (
          <div className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
            <AssignedStaffCell
              taskId={task?.id || null}
              teamId={assignedTeamId || null}
              staffId={assignedStaffId || null}
              teamName={assignedTeamName}
              staffName={assignedStaffName}
              className={assignedStaffClassName}
              teamClassName={teamClassName}
              staffClassName={staffClassName}
              iconSize={assignedStaffIconSize}
              showCircle={assignedStaffShowCircle}
              notAssignedStaffClassName={notAssignedStaffClassName}
            />
          </div>
        );

        if (!task?.id) {
          return assignedStaffCell;
        }

        return (
          <AssignStaffModal
            taskId={task.id}
            {...(assignedStaffId ? { assignedStaffId } : {})}
            {...(assignedTeamId ? { teamId: assignedTeamId } : {})}
            trigger={assignedStaffCell}
          />
        );
      },
    },
    createdBy: {
      key: "createdBy",
      className: "text-center",
      header: t("requests.table.createdBy"),
      render: (_value, row) => {
        if (!row.createdByUserProfile) {
          return null;
        }

        return (
          <div className="flex flex-row items-center justify-center gap-2">
            <Avatar>
              <AvatarFallback className="bg-transparent">
                <CircleUserIcon className="h-8 w-8" />
              </AvatarFallback>
            </Avatar>
            <span className={createdByClassName}>
              {`${row.createdByUserProfile.firstName} ${row.createdByUserProfile.lastName}`}
            </span>
          </div>
        );
      },
      sortable: true,
    },
    paymentStatus: {
      key: "paymentStatus",
      sortable: true,
      header: t("requests.table.paymentStatus", "Payment"),
      className: "text-center",
      render: (_value, row) => {
        const paymentStatus = getEffectiveRequestPaymentStatus(row);
        const showNotPaidIndicator =
          isRequestPaymentRequired(row) &&
          (!paymentStatus ||
            paymentStatus === BillingPaymentStatus.NOT_REQUIRED);

        if (showNotPaidIndicator) {
          return (
            <div className="flex items-center justify-center">
              <span title={t("billing.notPaid", "Not paid")}>
                <CircleAlert className="text-amber-500" />
              </span>
            </div>
          );
        }

        return (
          <PaymentStatusCell
            applyBadge={applyPaymentStatusBadge}
            paymentStatus={paymentStatus || BillingPaymentStatus.NOT_REQUIRED}
          />
        );
      },
    },
    actions: {
      key: "actions",
      header: t("requests.table.actions"),
      className: "text-center",
      render: (_value, row) => {
        let assignedToStaffIds: string[] = [];

        if (Array.isArray(row.assignedToStaffId)) {
          assignedToStaffIds = row.assignedToStaffId;
        } else if (row.assignedToStaffId) {
          assignedToStaffIds = [row.assignedToStaffId];
        }

        return (
          <RequestActionsCell
            bookingId={row.bookingId || ""}
            assignedToStaffIds={assignedToStaffIds}
            actionsEditIcon={actionsEditIcon}
            row={row}
            onEditRequest={onEditRequest}
            isChatWithGuest={isChatWithGuest}
            showChatAction={showChatAction}
          />
        );
      },
    },
  };

  const columns = visibleColumns.map((columnKey) => allColumns[columnKey]);

  return (
    <DataTable
      data={requests}
      columns={columns}
      loading={isLoading}
      onRowClick={onRowClick}
      emptyMessage={t("requests.table.noRequestsFound")}
      withPagination={withPagination}
      withSorting={withSorting}
      {...(withPagination
        ? {
            pagination: {
              currentPage: currentPage!,
              totalPages: totalPages!,
              onPageChange: onPageChange!,
            },
          }
        : {})}
      {...(withSorting
        ? {
            sorting: {
              field: sortField!,
              direction: sortDirection!,
              onSort: onSort!,
            },
          }
        : {})}
    />
  );
};
